import type { NavigationGuard, RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { isArray } from "es-toolkit/compat";

import {
  APP_CALENDARS_ROUTES,
  APP_CHAT_ROUTES,
  APP_PATIENTS_VIEW_CLINICALDOCUMENTATION_ROUTES,
  APP_ROUTES,
} from "@/Generated/Routes/paths";
import type { ChatRoom } from "@/Modules/Chat/Models/Domain/Chat";
import { Language } from "@/Common/Models/Domain/Language";

import useAuthStore from "@/Modules/Auth/Store";
import useTherapistStore from "@/Modules/Therapist/Store";
import useUserStore from "@/Modules/User/Store";
import useChatStore from "@/Modules/Chat/Store";
import useAppStore from "@/Modules/App/Store";
import useCustomerStore from "@/Modules/Customers/Store";
import usePushNotificationsStore from "@/Modules/PushNotifications/Store";
import useSubscriptionStore from "@/Modules/Subscription/Store";
import useActivityCenterStore from "@/Modules/ActivityCenter/Store";
import useQuestionnaireStore from "@/Modules/Questionnaire/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";
import useProfessionalViewStore from "@/Modules/Professional/Store";
import useCalendarsStore from "@/Modules/Calendars/Store";
import useOnBoardingStore from "@/Modules/OnBoarding/Store";
import useSignDocumentsStore from "@/Modules/SignDocuments/Store";

import { isValidString } from "@/Utils/String";
import { hasValue, isObject } from "@/Utils/Objects";
import { isTokenWillExpireSoon } from "@/Utils/JWT";
import { isApp } from "@/Utils/Ionic";
import { waitFor } from "@/Utils/Promises";
import { log } from "@/Utils/Debug";

import { config } from "@/Common/envConfig";
import { cleanCanonical } from "./utils";

export type NavigationGuardReturn = RouteLocationRaw | undefined | false;
export type GuardFunction = (
  to: RouteLocationNormalized,
  from?: RouteLocationNormalized
) => Promise<NavigationGuardReturn>;

export const buildBeforeEnterChain =
  (guards: GuardFunction[] | GuardFunction): NavigationGuard =>
  async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    let i = 0;
    let result: NavigationGuardReturn = undefined;
    const list = isArray(guards) ? guards : [guards];

    while (!hasValue(result) && i < list.length) {
      result = await list[i++]!(to, from);
    }

    if (hasValue(result)) {
      log(
        `GUARD_REDIRECT -> TO: ${to.path}, FROM: ${from.path}, RESULT_TO: ${(result as RouteLocationNormalized).name as string}`
      );

      return result;
    } else {
      return undefined;
    }
  };

export function authGuard(to: RouteLocationNormalized, _from: RouteLocationNormalized) {
  const authStore = useAuthStore();
  const isUserLogged = authStore.isUserLogged;
  const isPrivateRoute = !isObject(to.meta) || !to.meta.isPublic;

  if (isPrivateRoute) {
    if (isUserLogged) {
      return undefined;
    } else {
      return { name: APP_ROUTES.login.root.name };
    }
  } else {
    if (to.path === APP_ROUTES.login.root.path && isUserLogged) {
      return { name: APP_ROUTES.home.root.name };
    } else {
      return undefined;
    }
  }
}

export function notificationsGuard(to: RouteLocationNormalized) {
  const isPrivateRoute = !to.meta?.isPublic;
  const userStore = useUserStore();
  const therapistStore = useTherapistStore();

  if (isPrivateRoute) {
    userStore.clearNotification();
    therapistStore.clearNotification();
  }
}

export const initWSGuard: GuardFunction = async () => {
  const chatStore = useChatStore();

  chatStore.initializeWS();

  return undefined;
};

export const initLoguedAppGuard = async () => {
  const authStore = useAuthStore();
  const appStore = useAppStore();
  const pushStore = usePushNotificationsStore();
  const userStore = useUserStore();
  const subscriptionStore = useSubscriptionStore();
  const therapistStore = useTherapistStore();
  const activityCenterStore = useActivityCenterStore();
  const questionnaireStore = useQuestionnaireStore();
  const videoCallStore = useVideoCallStore();
  const chatStore = useChatStore();
  const professionalViewStore = useProfessionalViewStore();
  const calendarsStore = useCalendarsStore();
  const onboardingStore = useOnBoardingStore();
  const signDocumentsStore = useSignDocumentsStore();

  const calls: Promise<unknown>[] = [];

  if (isValidString(authStore.userToken) && isTokenWillExpireSoon(authStore.userToken, 7)) {
    await authStore.refreshToken();
  }

  if (isApp() && !hasValue(userStore.user) && authStore.isUserLogged) {
    pushStore.init();
  }

  if (await userStore.setUser(true)) {
    chatStore.initializeWS();

    calls.push(questionnaireStore.fetchInitial());
    calls.push(activityCenterStore.getNumberOfNewNotifications());
    calls.push(videoCallStore.checkVideoCallInProgress());
    calls.push(calendarsStore.fetchCalendars());
    calls.push(onboardingStore.setOptionsData(userStore.user?.language || Language.SPANISH));

    if (userStore.getUserInTherapistShow && isValidString(userStore.user!.therapistId)) {
      calls.push(professionalViewStore.fetchProfessionalInfo(userStore.user!.therapistId || ""));
    }

    if (userStore.getUserIsPatient) {
      calls.push(subscriptionStore.setSubscriptions()); /* TOOLBAR */
      calls.push(userStore.getB2bDocs());
      calls.push(signDocumentsStore.fetchSignDocumentsStats());
    } else {
      calls.push(therapistStore.setTherapist());
    }
  } else {
    appStore.clearStores();
    await waitFor(0.3);

    return { name: APP_ROUTES.login.root.name };
  }

  await Promise.all(calls);

  if (userStore.getUserIsPatient && hasValue(userStore.user) && userStore.getUserInOnBoarding) {
    return { name: APP_ROUTES.onboarding.root.name };
  }

  return undefined;
};

export const chatCustomerGuard: GuardFunction = async (to: RouteLocationNormalized) => {
  const chatStore = useChatStore();
  const appStore = useAppStore();
  const userStore = useUserStore();
  const customerStore = useCustomerStore();

  appStore.openSideMenu();

  await userStore.setUser();

  if (chatStore.chatRooms.length === 0) {
    await chatStore.fetchChats();
  }

  let chatRoom: ChatRoom | undefined = undefined;

  if (userStore.getUserIsTherapist) {
    chatRoom = chatStore.chatRooms.find((c) => c.user.id === to.params.customerId);
  } else if (chatStore.chatRooms.length > 0) {
    chatRoom = chatStore.chatRooms[0];
  }

  if (chatRoom) {
    const calls: Promise<unknown>[] = [chatStore.setActiveChatRoom(chatRoom.id)];

    if (userStore.getUserIsTherapist) {
      calls.push(customerStore.fetchCustomer(chatRoom.user.id));
    }

    await Promise.all(calls);

    return undefined;
  } else {
    return { name: APP_CHAT_ROUTES.root.name };
  }
};

export const patientClinicalDocumentationSessionGuard: GuardFunction = async (
  to: RouteLocationNormalized
) => {
  if (!isValidString(to.params.patientId) || !isValidString(to.params.sessionId)) {
    return { name: APP_PATIENTS_VIEW_CLINICALDOCUMENTATION_ROUTES.root.name };
  }

  return undefined;
};

export const enableNavigationGuard = (_to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
  const appStore = useAppStore();

  appStore.showNavigation();

  return undefined;
};

export const isTherapistGuard: GuardFunction = async () => {
  const userStore = useUserStore();

  if (userStore.getUserIsTherapist) {
    return undefined;
  }

  return { name: APP_ROUTES.home.root.name };
};

export const isPatientGuard: GuardFunction = async () => {
  const userStore = useUserStore();

  if (userStore.getUserIsPatient) {
    return undefined;
  }

  return { name: APP_ROUTES.home.root.name };
};

export const onlyLanguageGuard =
  (langs: Language[]): GuardFunction =>
  async () => {
    const userStore = useUserStore();

    if (isValidString(userStore.user?.language) && langs.includes(userStore.user?.language)) {
      return undefined;
    }

    return { name: APP_ROUTES.home.root.name };
  };

export const loadCannonicalGuard: GuardFunction = async (to: RouteLocationNormalized) => {
  let canonical = config.appUrl;

  if (isValidString(to.meta?.canonical)) {
    canonical += cleanCanonical(to.meta.canonical);

    log(`Canonical: ${canonical}`);
  }

  to.meta.canonical = canonical;

  return undefined;
};

export const canSyncCalendars: GuardFunction = async () => {
  const therapistStore = useTherapistStore();

  if (therapistStore.therapist?.calendarIntegrations === true) {
    return undefined;
  }

  return { name: APP_CALENDARS_ROUTES.root.name };
};

export const onlyTherapistGuard: GuardFunction = async () => {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  if (!isValidString(authStore.userToken)) return { name: APP_ROUTES.login.root.name };

  if (!hasValue(userStore.user)) {
    await userStore.setUser(true);
  }

  if (!userStore.getUserIsTherapist) {
    return { name: APP_ROUTES.home.root.name };
  }

  return undefined;
};

export const onlyPatientGuard: GuardFunction = async () => {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  if (!isValidString(authStore.userToken)) return { name: APP_ROUTES.login.root.name };

  if (!hasValue(userStore.user)) {
    await userStore.setUser(true);
  }

  if (!userStore.getUserIsPatient) {
    return { name: APP_ROUTES.home.root.name };
  }

  return undefined;
};
